/**
 * apps/web :: lib/api
 *
 * Thin fetch wrapper for calls that need the auth cookie. Every auth-touching
 * request must run with `credentials: 'include'` so the `rb_session` cookie
 * rides along — omitting it is the #1 way to accidentally break sign-in.
 *
 * Base URL comes from `VITE_API_BASE_URL` (see `.env.example`). If empty we
 * fall back to same-origin, which is correct when the SPA is served from the
 * Ktor host itself (prod reverse-proxy setup); in local dev the backend runs
 * on a different port so the env var is required.
 *
 * Wave 14k (ROADS-348): on every response we capture the `X-Request-Id`
 * header into the error-reporter module so any subsequent client-side
 * error can be correlated with the most-recent backend call. Failure to
 * import that module (e.g. tree-shaken in a non-app build) is harmless —
 * the helper is a no-op if undefined.
 */
import { setLastApiRequestId } from "@/lib/error-reporter";

export const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

/**
 * Error thrown by {@link apiFetch} on any non-2xx response. `status` is the
 * HTTP code; `message` is server-supplied when JSON-decodable, otherwise the
 * HTTP status text. Use `status === 401` to drive redirect-to-login UX.
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;
  constructor(status: number, message: string, body: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/**
 * Build a fully-qualified URL from a path. Accepts both `/auth/me` and
 * `https://host/auth/me`; absolute inputs pass through unchanged so callers
 * can point at a staging backend on demand.
 */
export function resolveUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${suffix}`;
}

/**
 * Fetch wrapper that always sends credentials and parses JSON on success.
 *
 * - Adds `Accept: application/json` if the caller didn't set it.
 * - On 204 returns `undefined as T` — convenient for logout-style endpoints.
 * - On 4xx/5xx throws {@link ApiError}; the caller decides whether to show
 *   an error toast or redirect to `/login`.
 */
export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (
    init.body !== undefined &&
    init.body !== null &&
    typeof init.body === "string" &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(resolveUrl(path), {
    ...init,
    credentials: "include",
    headers,
  });

  // Capture correlation id for the error reporter (Wave 14k). Best-effort:
  // header may be absent if the request short-circuited or the backend
  // doesn't echo it. We never throw from here.
  const requestId = response.headers.get("X-Request-Id");
  if (requestId) {
    try {
      setLastApiRequestId(requestId);
    } catch {
      // never let observability break the request path
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    const message =
      (typeof parsed === "object" && parsed !== null && "message" in parsed &&
        typeof (parsed as { message: unknown }).message === "string"
        ? (parsed as { message: string }).message
        : null) ??
      (typeof parsed === "object" && parsed !== null && "error" in parsed &&
        typeof (parsed as { error: unknown }).error === "string"
        ? (parsed as { error: string }).error
        : null) ??
      response.statusText ??
      `HTTP ${response.status}`;
    throw new ApiError(response.status, message, parsed);
  }

  return parsed as T;
}
